if (!customElements.get('choose-your-battery')) {
  customElements.define(
    'choose-your-battery',
    class ChooseYourBattery extends HTMLElement {
      variantChangeUnsubscriber = undefined;
      optionChangeUnsubscriber = undefined;
      pinnedScrollY = null;

      connectedCallback() {
        this.cards = Array.from(this.querySelectorAll('[data-cyb-card]'));
        if (!this.cards.length) return;

        this.updateActiveCard();

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => {
            this.updateActiveCard();
            this.restorePinnedScroll();
          });
          this.optionChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, () => {
            this.updateActiveCard();
            this.restorePinnedScroll();
          });
        }

        document.addEventListener('change', this.onDocumentChange);
        this.addEventListener('click', this.onSelectClick);
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
        if (this.optionChangeUnsubscriber) this.optionChangeUnsubscriber();
        document.removeEventListener('change', this.onDocumentChange);
        this.removeEventListener('click', this.onSelectClick);
        this.clearPinnedScroll();
      }

      onDocumentChange = (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement)) return;
        if (!input.closest('.battery-swatch')) return;
        this.updateActiveCard();
      };

      onSelectClick = (event) => {
        const trigger = event.target.closest('[data-cyb-select]');
        if (!trigger || !this.contains(trigger)) return;

        const card = trigger.closest('[data-cyb-card]');
        if (!card) return;

        event.preventDefault();
        this.selectBatteryForCard(card);
      };

      getSelectedBatteryMeta() {
        const checked =
          document.querySelector('.swatch-container.battery-swatch input:checked') ||
          document.querySelector('.battery-swatch input:checked');

        if (checked) {
          return {
            handle: (checked.getAttribute('data-option-handle') || checked.value || '').trim().toLowerCase(),
            label: (checked.value || checked.getAttribute('data-option-handle') || '').trim().toLowerCase(),
          };
        }

        // Fallback: battery product PDP (no Battery option) — match product title/handle
        return {
          handle: (this.getAttribute('data-product-handle') || '').trim().toLowerCase(),
          label: (this.getAttribute('data-product-title') || '').trim().toLowerCase(),
        };
      }

      getBatteryInputs() {
        return document.querySelectorAll(
          '.swatch-container.battery-swatch input[type="radio"], .battery-swatch input[type="radio"]'
        );
      }

      normalize(value) {
        return (value || '')
          .toString()
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-');
      }

      valuesMatch(a, b) {
        if (!a || !b) return false;
        return a === b || a.includes(b) || b.includes(a);
      }

      matchesSelected(card, selected) {
        const matchHandle = this.normalize(card.getAttribute('data-match'));
        const matchLabel = this.normalize(card.getAttribute('data-match-label'));
        const selectedHandle = this.normalize(selected.handle);
        const selectedLabel = this.normalize(selected.label);

        if (!selectedHandle && !selectedLabel) return false;

        const candidates = [matchHandle, matchLabel].filter(Boolean);
        const selectedValues = [selectedHandle, selectedLabel].filter(Boolean);

        return candidates.some((candidate) =>
          selectedValues.some((selectedValue) => this.valuesMatch(selectedValue, candidate))
        );
      }

      findBatteryInputForCard(card) {
        const matchHandle = this.normalize(card.getAttribute('data-match'));
        const matchLabel = this.normalize(card.getAttribute('data-match-label'));
        const candidates = [matchHandle, matchLabel].filter(Boolean);
        if (!candidates.length) return null;

        const inputs = Array.from(this.getBatteryInputs());
        return (
          inputs.find((input) => {
            if (input.disabled || input.classList.contains('disabled')) return false;
            const handle = this.normalize(input.getAttribute('data-option-handle') || '');
            const label = this.normalize(input.value || '');
            const selectedValues = [handle, label].filter(Boolean);
            return candidates.some((candidate) =>
              selectedValues.some((selectedValue) => this.valuesMatch(selectedValue, candidate))
            );
          }) || null
        );
      }

      pinScroll() {
        this.pinnedScrollY = window.scrollY;
        // Hold position through async variant/media updates
        window.clearTimeout(this.pinnedScrollTimer);
        this.pinnedScrollTimer = window.setTimeout(() => this.clearPinnedScroll(), 1200);
      }

      restorePinnedScroll() {
        if (this.pinnedScrollY == null) return;
        const top = this.pinnedScrollY;
        const restore = () => {
          if (Math.abs(window.scrollY - top) > 1) {
            window.scrollTo({ top });
          }
        };
        restore();
        requestAnimationFrame(restore);
      }

      clearPinnedScroll() {
        this.pinnedScrollY = null;
        window.clearTimeout(this.pinnedScrollTimer);
        this.pinnedScrollTimer = undefined;
      }

      selectBatteryForCard(card) {
        const input = this.findBatteryInputForCard(card);
        if (!input) return;

        this.pinScroll();

        if (!input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }

        this.updateActiveCard();
        this.restorePinnedScroll();
      }

      updateActiveCard() {
        const selected = this.getSelectedBatteryMeta();
        let hasActive = false;
        let activeSide = '';

        this.cards.forEach((card) => {
          const isActive = this.matchesSelected(card, selected);
          card.classList.toggle('is-active', isActive);
          card.classList.toggle('is-inactive', !isActive);
          card.setAttribute('aria-current', isActive ? 'true' : 'false');

          const selectBtn = card.querySelector('[data-cyb-select]');
          if (selectBtn) {
            selectBtn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          }

          if (isActive) {
            hasActive = true;
            activeSide = card.getAttribute('data-cyb-side') || '';
          }
        });

        if (!hasActive) {
          this.cards.forEach((card) => {
            card.classList.add('is-inactive');
            card.classList.remove('is-active');
            card.setAttribute('aria-current', 'false');
            const selectBtn = card.querySelector('[data-cyb-select]');
            if (selectBtn) selectBtn.setAttribute('aria-pressed', 'false');
          });
          activeSide = '';
        }

        if (activeSide) {
          this.setAttribute('data-active-side', activeSide);
        } else {
          this.removeAttribute('data-active-side');
        }
      }
    }
  );
}
